/**
 * @author Angel Labrada
 * @since v0.0.1
 * @date 28/11/21
 */
import {useMutation, useQueryClient} from 'react-query';
import useAlerts from '@/hooks/useAlerts';
import SectionsApiService from '@/modules/templates/services/SectionsApiService';
import {CACHE_KEY_LIST_SECTIONS} from '@/modules/templates/contants/sections';
import {useAuditData} from '@/modules/auditorAudits/hooks/useAuditData';
import {useEffect} from 'react';
import {CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS} from '@/modules/auditorAudits/contants/auditorAudits';
import {useParams} from 'react-router-dom';
import {CACHE_KEY_AUDITS} from '@/modules/audits/contants/audits';
import {useRunAuditOnlineStatus} from '@/contexts/RunAuditOnlineStatusProvider';

export const useCrudSections = () => {
  const queryClient = useQueryClient();
  const {id} = useParams();
  const {putErrorCode} = useAlerts();
  let prevTitleData;
  const {auditOnline} = useRunAuditOnlineStatus();


  const {mutateAsync: create, isLoading: isCreating} = useMutation(SectionsApiService.save,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_SECTIONS);
        queryClient.invalidateQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
      },
      onError: (e) => {
        auditOnline && putErrorCode(e);      }
    });

  const {mutateAsync: update, isLoading: isUpdating} = useMutation(SectionsApiService.update,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        const prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        prevTitleData = prevData;

        if(newValue && newValue?.title && prevData.length){
          prevData.find((item) => item._id === newValue._id).title = newValue.title;
        }

        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevTitleData;
        });
        auditOnline && putErrorCode(e);      }
    });

  const {mutateAsync: remove, isLoading: isRemoving} = useMutation(SectionsApiService.delete,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_SECTIONS);
        queryClient.invalidateQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        queryClient.invalidateQueries([CACHE_KEY_AUDITS, id]);
      },
      onError: () => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevTitleData;
        });
      },
      throwOnError: true
    }
  );

  const {mutateAsync: duplicate, isLoading: isDuplicating} = useMutation(SectionsApiService.duplicate,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_SECTIONS);
        queryClient.invalidateQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevTitleData;
        });
        auditOnline && putErrorCode(e);      }
    });

  const {mutateAsync: changeOrder, isLoading: isChanging} = useMutation(SectionsApiService.changeSectionOrder,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_SECTIONS);
      },
      onError: (e) => {

        auditOnline && putErrorCode(e);      }
    }
  );

  return {
    create,
    isCreating,
    update,
    isUpdating,
    remove,
    isRemoving,
    changeOrder,
    isChanging,
    duplicate,
    isDuplicating,
    isLoading: isCreating || isUpdating || isRemoving || isChanging || isDuplicating
  };
};

export const useCrudQuestions = (sectionId, questionId) => {
  const queryClient = useQueryClient();
  const {/*dataSections, setDataSections,*/ setSaving} = useAuditData();
  const {putErrorCode} = useAlerts();
  const {id} = useParams();
  const {auditOnline} = useRunAuditOnlineStatus();

  const {mutateAsync: create, isLoading: isCreating} = useMutation(SectionsApiService.addQuestion,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CACHE_KEY_LIST_SECTIONS, sectionId, questionId]);
        queryClient.invalidateQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
      },
      onError: (e) => {
        auditOnline && putErrorCode(e);
      }
    });

  const {mutateAsync: update, isLoading: isUpdating} = useMutation(SectionsApiService.updateQuestion,
    {
      onMutate: async newAnswer => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        let prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        if (newAnswer && newAnswer?.answer && newAnswer?.answer?.section && newAnswer?.readingRegistry && newAnswer?.readingRegistry?.placesMeasure) {
          prevData = prevData.map((item) => {
            if (item?._id === newAnswer?.answer?.section) {
              item.questions = item.questions.map((question) => {
                if (question?._id === newAnswer?.answer?.question) {
                  if (question?.readingRegistry) {
                    question.readingRegistry.placesMeasure = newAnswer?.readingRegistry?.placesMeasure;
                  }
                }
                return question;
              });
            }
            return item;
          });
        }

        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevData;
        });

        return { prevData };
      },
      onError: (e) => {
        auditOnline && putErrorCode(e);      }
    });

  const {mutateAsync: remove, isLoading: isRemoving} = useMutation(SectionsApiService.deleteQuestion,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CACHE_KEY_LIST_SECTIONS, sectionId, questionId]);
        queryClient.invalidateQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
      },
      throwOnError: true
    }
  );

  const {mutateAsync: changeOrder, isLoading: isChanging} = useMutation(SectionsApiService.changeQuestionOrder,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CACHE_KEY_LIST_SECTIONS, sectionId, questionId]);
      },
      onError: (e) => {
        auditOnline && putErrorCode(e);      }
    }
  );

  useEffect(() => setSaving(isCreating || isUpdating || isRemoving || isChanging), [isChanging, isCreating, isRemoving, isUpdating, setSaving]);

  return {
    create,
    isCreating,
    update,
    isUpdating,
    remove,
    isRemoving,
    changeOrder,
    isChanging,
    isLoading: isCreating || isUpdating || isRemoving || isChanging
  };
};

export const useCrudCoverFields = () => {
  const queryClient = useQueryClient();
  const {auditOnline} = useRunAuditOnlineStatus();
  const {putErrorCode} = useAlerts();

  const {mutateAsync: create, isLoading: isCreating} = useMutation(SectionsApiService.addField,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_SECTIONS);
      },
      onError: (e) => {
        putErrorCode(e);
      }
    });

  const {mutateAsync: update, isLoading: isUpdating} = useMutation(SectionsApiService.updateField,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_SECTIONS);
      },
      onError: (e) => {
        auditOnline && putErrorCode(e);      }
    });

  const {mutateAsync: remove, isLoading: isRemoving} = useMutation(SectionsApiService.deleteField,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_SECTIONS);
      },
      throwOnError: true
    }
  );

  const {mutateAsync: changeOrder, isLoading: isChanging} = useMutation(SectionsApiService.changeFieldOrder,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_SECTIONS);
      },
      onError: (e) => {
        auditOnline && putErrorCode(e);      }
    }
  );

  return {
    create,
    isCreating,
    update,
    isUpdating,
    remove,
    isRemoving,
    changeOrder,
    isChanging,
    isLoading: isCreating || isUpdating || isRemoving || isChanging
  };
};
